import dayjs from 'dayjs';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { InputMask, InputMaskChangeEvent } from 'primereact/inputmask';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Toast } from 'primereact/toast';
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { PartnerService, PartnerWithTransactions, TransactionMessage } from '../services/PartnerService';
import { Card } from 'primereact/card';

interface EditPartnerProps {
    partner: Partial<PartnerWithTransactions>;
    onSave: (partner: PartnerWithTransactions) => void;
}

export const EditPartner: React.FC<EditPartnerProps> = ({ partner, onSave }) => {
    const { t } = useTranslation();
    const { isAdmin } = useAuth();
    const [formData, setFormData] = useState<Partial<PartnerWithTransactions>>(partner);
    const [transactionMessage, setTransactionMessage] = useState<string>('');
    const [transactionNumber, setTransactionNumber] = useState<string>('1');
    const toast = useRef<Toast>(null);
    const op = useRef<OverlayPanel | null>(null);
    useEffect(() => {
        setFormData(partner);
    }, [partner]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement> | InputMaskChangeEvent) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSubmit = () => {
        onSave(formData as PartnerWithTransactions);
    };

    const handleAddTransaction = async () => {
        if (!transactionMessage.trim() || transactionNumber === '0') {
            return;
        }
        const newTransaction: TransactionMessage = {
            message: transactionMessage,
            tokenId: '',
            number: parseInt(transactionNumber)
        };
        if (formData.id) {
            await PartnerService.addTransactionMessage(formData.id, newTransaction);
            const updatedPartner = await PartnerService.getPartnerById(formData.id);
            setFormData(updatedPartner);
        }
        setTransactionMessage('');
        setTransactionNumber('1');
    };

    const formatDate = (date?: Date) => date ? dayjs(date).format('L') : '-';
    const copyLink = `${window.location.origin}/partnerpage/${formData.id}`;
    const handleCopy = (success: boolean) => {
        if (success) {
            toast.current?.show({ severity: 'success', summary: t('editToken.copySuccess'), life: 1000 });
        } else {
            toast.current?.show({ severity: 'error', summary: t('editToken.copyError'), life: 1000 });
        }
    };


    return (<>
        <Card style={{ margin: "2em" }} title={t('editPartner.title')}>
            <div className="p-fluid">
                <Toast ref={toast} />
                <div className="p-field">
                    <label htmlFor="name">{t('editPartner.name')}</label>
                    <InputText id="name" name="name" value={formData.name || ''} onChange={handleChange} />
                </div>
                <div className="p-field" style={{ marginTop: '6px' }}>
                    <label htmlFor="email">{t('editPartner.email')}</label>
                    <InputText id="email" name="email" value={formData.email || ''} onChange={handleChange} />
                </div>
                <div className="p-field" style={{ marginTop: '6px' }}>
                    <label htmlFor="telephone">{t('editPartner.telephone')}</label>
                    <InputMask id="telephone" name="telephone" mask="99/9999?99999" value={formData.telephone || ''} onChange={handleChange} />
                </div>
                {formData.id &&
                    (<div className="p-field mt-10" >
                        <label htmlFor="copyLink">{t('editPartner.copyLink')}</label>
                        <IconField>

                            <InputText id="copyLink" value={copyLink} readOnly />
                            <CopyToClipboard text={copyLink} onCopy={(text, result) => handleCopy(result)}>
                                <InputIcon className="pi pi-copy" type="button" />
                            </CopyToClipboard>
                        </IconField>
                    </div>)}

                <div className="p-fluid grid mt-1">
                    <div className="col-12 md:col-6">
                        <Button label={t('editPartner.submit')} icon="pi pi-check" onClick={handleSubmit} />
                    </div>
                </div>
            </div>
        </Card>
        {formData.id &&
        <Card style={{ margin: "2em" }} title={`${formData.name} - ${t('editPartner.numberOfTokens')} ${formData.numberOfTokens || 0}`}>
            <div className="p-fluid">
                <div className="p-field" style={{ marginTop: '6px' }}>
                    <label htmlFor="transactionMessages">{t('editPartner.transactionMessages')}</label>
                    <DataTable
                        value={formData.transactionMessages || []}
                        rows={4}
                        scrollable
                        size="small"
                        scrollHeight="200px"
                        sortField="createdDateTime"
                        sortOrder={-1}
                    >
                        <Column field="message" header={t('editPartner.message')} sortable />
                        <Column field="tokenId" header={t('editPartner.tokenId')} />
                        <Column field="number" header={t('editPartner.number')} sortable />
                        <Column
                            field="createdDateTime"
                            header={t('editPartner.createDateTime')}
                            body={(rowData) => formatDate(rowData.createdDateTime)}
                            sortable
                        />
                    </DataTable>
                </div>
                <OverlayPanel ref={op}>
                    <div className="p-field">
                        <label htmlFor="newTransactionMessage">{t('editPartner.newTransactionMessage')}</label>
                        <InputText
                            id="newTransactionMessage"
                            value={transactionMessage}
                            onChange={(e) => setTransactionMessage(e.target.value)}
                        />
                    </div>
                    <div className="p-field" style={{ marginTop: '6px' }}>
                        <label htmlFor="newTransactionNumber">{t('editPartner.newTransactionNumber')}</label>
                        <InputText
                            id="newTransactionNumber"
                            type="number"
                            inputMode='numeric'
                            value={transactionNumber}
                            onChange={(e) => setTransactionNumber(e.target.value)}
                        />
                    </div>
                    <Button
                        label={t('editPartner.confirmAddTransaction')}
                        icon="pi pi-check"
                        onClick={() => {
                            handleAddTransaction();
                            op.current?.hide();
                        }}
                    />
                </OverlayPanel>
                <div className="p-fluid grid mt-1">
                    {formData.id && isAdmin &&
                        <div className="col-12 md:col-6">
                            <Button label={t('editPartner.addTransaction')} icon="pi pi-plus" onClick={(e) => op.current?.toggle(e)} />
                        </div>
                    }
                </div>
            </div>
        </Card >}
    </>
    );
};
